import React, { useState, useEffect, useCallback } from 'react';
import { Package, Plus, MapPin, Calendar, Thermometer, Clock, ChevronRight, CheckCircle, XCircle, AlertTriangle, Truck, Eye, Trash2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import CreateShipmentModal from './CreateShipmentModal';
import ShipmentDetailModal from './ShipmentDetailModal';

interface Shipment {
  id: string;
  device_id: string;
  device_nickname: string;
  name: string;
  description: string | null;
  origin: string;
  destination: string;
  expected_days: number;
  started_at: string;
  expected_arrival: string;
  temperature_min: number | null;
  temperature_max: number | null;
  status: 'active' | 'completed' | 'cancelled';
  created_at: string;
  completed_at: string | null;
  days_elapsed: number;
  event_count: number;
  latest_event_type: string | null;
  latest_event_severity: string | null;
  latest_location: string | null;
}

interface Device {
  device_id: string;
  device_nickname: string;
}

function ShipmentsPage() {
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState<Shipment | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const loadShipments = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('get_customer_shipments', {
        p_status: statusFilter,
        p_limit: 50
      });

      if (error) throw error;
      setShipments(data || []);
    } catch (error) {
      console.error('Error loading shipments:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const loadDevices = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('get_user_devices');
      if (error) throw error;
      setDevices((data || []).map((d: any) => ({
        device_id: d.device_id,
        device_nickname: d.device_nickname || d.device_id
      })));
    } catch (error) {
      console.error('Error loading devices:', error);
    }
  }, []);

  useEffect(() => {
    loadShipments();
    loadDevices();
  }, [loadShipments, loadDevices]);

  const handleCompleteShipment = async (shipmentId: string) => {
    try {
      const { error } = await supabase.rpc('complete_shipment', {
        p_shipment_id: shipmentId
      });
      if (error) throw error;
      await loadShipments();
    } catch (error) {
      console.error('Error completing shipment:', error);
    }
  };

  const handleCancelShipment = async (shipmentId: string) => {
    try {
      const { error } = await supabase.rpc('cancel_shipment', {
        p_shipment_id: shipmentId
      });
      if (error) throw error;
      await loadShipments();
    } catch (error) {
      console.error('Error cancelling shipment:', error);
    }
  };

  const handleDeleteShipment = async (shipmentId: string) => {
    try {
      const confirm = window.confirm(
        'Delete this shipment? This will also remove all its events. This action cannot be undone.'
      );
      if (!confirm) return;
      const { error } = await supabase.from('shipments').delete().eq('id', shipmentId);
      if (error) throw error;
      await loadShipments();
    } catch (error) {
      console.error('Error deleting shipment:', error);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <Truck className="w-3 h-3 mr-1" />
            In Transit
          </span>
        );
      case 'completed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <CheckCircle className="w-3 h-3 mr-1" />
            Completed
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <XCircle className="w-3 h-3 mr-1" />
            Cancelled
          </span>
        );
      default:
        return null;
    }
  };

  const getSeverityIndicator = (severity: string | null) => {
    if (!severity) return null;
    switch (severity) {
      case 'critical':
        return <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />;
      case 'warning':
        return <div className="w-2 h-2 bg-yellow-500 rounded-full" />;
      default:
        return <div className="w-2 h-2 bg-green-500 rounded-full" />;
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getProgress = (shipment: Shipment) => {
    if (shipment.status !== 'active') return 100;
    const progress = (shipment.days_elapsed / shipment.expected_days) * 100;
    return Math.min(progress, 100);
  };

  const activeShipments = shipments.filter(s => s.status === 'active');
  const completedShipments = shipments.filter(s => s.status === 'completed');
  const cancelledShipments = shipments.filter(s => s.status === 'cancelled');

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Shipments</h1>
          <p className="text-gray-500 mt-1">Track and monitor your active shipments with AI-powered insights</p>
        </div>
        <button
          onClick={() => setCreateModalOpen(true)}
          className="flex items-center px-4 py-2 bg-traqpack-yellow text-gray-900 font-medium rounded-lg hover:bg-yellow-400 transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          New Shipment
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Active Shipments</p>
              <p className="text-2xl font-bold text-gray-900">{activeShipments.length}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Truck className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Completed</p>
              <p className="text-2xl font-bold text-gray-900">{completedShipments.length}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Alerts</p>
              <p className="text-2xl font-bold text-gray-900">
                {activeShipments.filter(s => s.latest_event_severity === 'warning' || s.latest_event_severity === 'critical').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex space-x-2 mb-4">
        <button
          onClick={() => setStatusFilter(null)}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            statusFilter === null
              ? 'bg-gray-900 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All ({shipments.length})
        </button>
        <button
          onClick={() => setStatusFilter('active')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            statusFilter === 'active'
              ? 'bg-green-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Active Shipments ({activeShipments.length})
        </button>
        <button
          onClick={() => setStatusFilter('completed')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            statusFilter === 'completed'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Completed ({completedShipments.length})
        </button>
      </div>

      {/* Shipments List */}
      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12">
          <div className="flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-traqpack-yellow mb-4"></div>
            <p className="text-gray-500">Loading shipments...</p>
          </div>
        </div>
      ) : shipments.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12">
          <div className="flex flex-col items-center justify-center">
            <Package className="w-16 h-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No shipments yet</h3>
            <p className="text-gray-500 mb-4">Create your first shipment to start AI-powered tracking</p>
            <button
              onClick={() => setCreateModalOpen(true)}
              className="flex items-center px-4 py-2 bg-traqpack-yellow text-gray-900 font-medium rounded-lg hover:bg-yellow-400 transition-colors"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create Shipment
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {shipments.map((shipment) => (
            <div
              key={shipment.id}
              className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    {getSeverityIndicator(shipment.latest_event_severity)}
                    <h3 className="text-lg font-semibold text-gray-900">{shipment.name}</h3>
                    {getStatusBadge(shipment.status)}
                  </div>

                  {shipment.description && (
                    <p className="text-sm text-gray-500 mb-3">{shipment.description}</p>
                  )}

                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-3">
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 mr-1 text-gray-400" />
                      <span>{shipment.origin}</span>
                      <ChevronRight className="w-4 h-4 mx-1 text-gray-400" />
                      <span>{shipment.destination}</span>
                    </div>
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                      <span>Started {formatDate(shipment.started_at)}</span>
                    </div>
                    {(shipment.temperature_min !== null || shipment.temperature_max !== null) && (
                      <div className="flex items-center">
                        <Thermometer className="w-4 h-4 mr-1 text-gray-400" />
                        <span>
                          {shipment.temperature_min !== null ? `${shipment.temperature_min}°C` : '--'} - {shipment.temperature_max !== null ? `${shipment.temperature_max}°C` : '--'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Progress bar for active shipments */}
                  {shipment.status === 'active' && (
                    <div className="mb-3">
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                        <span>Day {shipment.days_elapsed} of {shipment.expected_days}</span>
                        <span>ETA: {formatDate(shipment.expected_arrival)}</span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            getProgress(shipment) > 100 ? 'bg-red-500' : 'bg-traqpack-yellow'
                          }`}
                          style={{ width: `${Math.min(getProgress(shipment), 100)}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Latest update */}
                  {shipment.latest_location && (
                    <div className="flex items-center text-sm text-gray-500">
                      <Clock className="w-4 h-4 mr-1" />
                      <span>Last seen: {shipment.latest_location}</span>
                    </div>
                  )}

                  <div className="text-xs text-gray-400 mt-2">
                    Device: {shipment.device_nickname} • {shipment.device_id} | {shipment.event_count} events
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col space-y-2 ml-4">
                  <button
                    onClick={() => {
                      setSelectedShipment(shipment);
                      setDetailModalOpen(true);
                    }}
                    className="flex items-center px-3 py-1.5 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    View
                  </button>
                 {shipment.status === 'active' && (
                   <button
                     onClick={() => handleCompleteShipment(shipment.id)}
                     className="flex items-center px-3 py-1.5 text-sm text-green-700 bg-green-100 hover:bg-green-200 rounded-lg transition-colors"
                   >
                     <CheckCircle className="w-4 h-4 mr-1" />
                     Complete
                   </button>
                 )}
                  <button
                    onClick={() => handleDeleteShipment(shipment.id)}
                    className="flex items-center px-3 py-1.5 text-sm text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Shipment Modal */}
      <CreateShipmentModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        devices={devices}
        onSuccess={() => {
          setCreateModalOpen(false);
          loadShipments();
        }}
      />

      {/* Shipment Detail Modal */}
      {selectedShipment && (
        <ShipmentDetailModal
          isOpen={detailModalOpen}
          onClose={() => {
            setDetailModalOpen(false);
            setSelectedShipment(null);
          }}
          shipment={selectedShipment}
          onComplete={handleCompleteShipment}
          onCancel={handleCancelShipment}
        />
      )}
    </div>
  );
}

export default ShipmentsPage;
